import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
} from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

const qualityBadgeClass = (value) => {
  if (value === "approved") return "bg-success";
  if (value === "rejected") return "bg-danger";
  return "bg-warning text-dark";
};

const readOnlyStyle = { backgroundColor: "#f8f9fa" };

const todayISO = () => new Date().toLocaleDateString("en-CA");

const lineAmount = (row) => {
  const qty = Number(row.qty);
  const unitPrice = Number(row.unit_price);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPrice)) return 0;
  return qty * unitPrice;
};

export default function Inward() {
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showAll, setShowAll] = useState(true);
  const [saving, setSaving] = useState(false);

  // GRN header
  const [grnNo, setGrnNo] = useState("");
  const [inwardDate, setInwardDate] = useState(todayISO());
  const [invoiceNo, setInvoiceNo] = useState("");
  const [supplier, setSupplier] = useState("");

  // Product picker + the line items built up before saving
  const [category, setCategory] = useState("");
  const [product, setProduct] = useState("");
  const [items, setItems] = useState([]);

  const [successMsg, setSuccessMsg] = useState("");

  const flash = (message) => {
    setSuccessMsg(message);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  useEffect(() => {
    fetchInwardEntries();

    // Fetch categories
    api
      .get("/api/product-categories", { withCredentials: true })
      .then((res) => setCategories(res.data.productCategories || []))
      .catch(() => setCategories([]));

    // Fetch products
    api
      .get("/api/products", { withCredentials: true })
      .then((res) => setAllProducts(res.data.products || []))
      .catch(() => setAllProducts([]));

    // Fetch suppliers - the GRN header supplier is chosen from this list
    api
      .get("/api/suppliers", { withCredentials: true })
      .then((res) => setSuppliers(res.data.suppliers || []))
      .catch(() => setSuppliers([]));
  }, []);

  const fetchInwardEntries = () => {
    setLoading(true);
    api
      .get("/api/inward", { withCredentials: true })
      .then((res) => {
        setEntries(res.data.inwardEntries || []);
      })
      .catch(() => {
        setEntries([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Preview only - the real number is assigned under a lock when the GRN saves.
  const fetchNextGrn = () => {
    api
      .get("/api/inward/next-grn", { withCredentials: true })
      .then((res) => setGrnNo(res.data.next_grn ?? ""))
      .catch(() => setGrnNo(""));
  };

  // Products belonging to the picker's current category.
  const productsForCategory = category
    ? allProducts.filter((p) => String(p.category_id) === String(category))
    : allProducts;

  // Selecting a product appends it to the table below, mirroring the paper GRN.
  const handleProductChange = (productId) => {
    setProduct("");
    if (!productId) return;

    const selected = allProducts.find((p) => p.id === parseInt(productId, 10));
    if (!selected) return;

    if (items.some((item) => item.product_id === selected.id)) {
      flash(`${selected.product_name} is already added to this GRN.`);
      return;
    }

    setItems((prev) => [
      ...prev,
      {
        product_id: selected.id,
        product_name: selected.product_name,
        product_description: selected.product_description || "",
        unit: selected.unit || "",
        qty: "",
        unit_price: selected.unit_price ?? "",
        remark: "",
      },
    ]);
  };

  const handleItemChange = (index, field, value) =>
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const removeItem = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  const resetForm = () => {
    setInwardDate(todayISO());
    setInvoiceNo("");
    setSupplier("");
    setCategory("");
    setProduct("");
    setItems([]);
  };

  const openForm = () => {
    resetForm();
    fetchNextGrn();
    setShowForm(true);
  };

  const grandTotal = items.reduce((sum, row) => sum + lineAmount(row), 0);

  const handleSave = () => {
    if (!inwardDate) return flash("Please select a date!");
    if (!supplier) return flash("Please select a supplier!");
    if (items.length === 0) return flash("Please add at least one product!");

    for (let i = 0; i < items.length; i += 1) {
      const row = items[i];
      if (!row.qty || Number(row.qty) <= 0) {
        return flash(`Please enter a valid quantity for ${row.product_name}!`);
      }
      if (row.unit_price === "" || Number(row.unit_price) < 0) {
        return flash(`Please enter a valid price for ${row.product_name}!`);
      }
    }

    setSaving(true);
    api
      .post(
        "/api/inward",
        {
          inward_date: inwardDate,
          invoice_no: invoiceNo,
          supplier_id: supplier,
          items: items.map((row) => ({
            product_id: row.product_id,
            product_description: row.product_description,
            qty: row.qty,
            unit_price: row.unit_price,
            remark: row.remark,
          })),
        },
        { withCredentials: true },
      )
      .then((res) => {
        flash(res.data?.message || "Inward saved successfully!");
        resetForm();
        setShowForm(false);
        setCurrentPage(1);
        fetchInwardEntries();
      })
      .catch((err) => {
        const data = err.response?.data;
        flash(data?.message || data?.error || "Error saving inward entry!");
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (item) => {
    const confirmed = window.confirm(
      "Delete this inward entry? This will also remove it from Stock and Stock Summary.",
    );
    if (!confirmed) return;

    api
      .delete("/api/inward/" + item.id, { withCredentials: true })
      .then(() => {
        fetchInwardEntries();
        flash("Inward entry deleted successfully!");
      })
      .catch((err) => {
        const data = err.response?.data;
        flash(data?.message || "Error deleting inward entry!");
      });
  };

  const filteredEntries = entries.filter((item) => {
    const term = search.toLowerCase();
    return (
      (item.product_name || "").toLowerCase().includes(term) ||
      (item.category_name || "").toLowerCase().includes(term) ||
      String(item.grn_no || "").includes(term) ||
      (item.invoice_no || "").toLowerCase().includes(term)
    );
  });

  const totalRows = filteredEntries.length;
  const totalPages = rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedEntries = showAll
    ? filteredEntries
    : filteredEntries.slice(startIndex, startIndex + rowsPerPage);

  return (
    <section>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Product Management", path: "/product" },
          { name: "Inward" },
        ]}
      />

      {successMsg && (
        <div
          className={`alert ${
            successMsg.toLowerCase().includes("error") ||
            successMsg.toLowerCase().includes("please") ||
            successMsg.toLowerCase().includes("already")
              ? "alert-danger"
              : "alert-success"
          }`}
        >
          {successMsg}
        </div>
      )}

      {!showForm && (
        <>
          <Card body className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <Button
                style={{ backgroundColor: "#663399", border: "none" }}
                onClick={openForm}
              >
                Inward Items
              </Button>

              <input
                type="text"
                placeholder="Search GRN / Invoice / Product / Category"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: "320px",
                  height: "38px",
                  paddingLeft: "10px",
                  border: "1px solid #d1d1d1",
                }}
              />
            </div>
          </Card>

          <Card body>
            <Card.Title className="mb-4">Product Management - Inward</Card.Title>

            <div style={{ overflowX: "auto" }}>
              <Table bordered hover className="align-middle">
                <thead style={{ backgroundColor: "#cfc7d9" }}>
                  <tr>
                    <th>No</th>
                    <th>GRN No</th>
                    <th>Date</th>
                    <th>Invoice No</th>
                    <th>Product Name</th>
                    <th>Category Name</th>
                    <th>Supplier</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Price</th>
                    <th>Quality Check</th>
                    <th>Remark</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="13" className="text-center">Loading...</td>
                    </tr>
                  )}

                  {!loading && paginatedEntries.length === 0 && (
                    <tr>
                      <td colSpan="13" className="text-center">No inward entries found.</td>
                    </tr>
                  )}

                  {!loading &&
                    paginatedEntries.map((item, index) => (
                      <tr key={item.id}>
                        <td>{startIndex + index + 1}</td>
                        <td>{item.grn_no || "-"}</td>
                        <td>
                          {item.inward_date
                            ? new Date(item.inward_date).toLocaleDateString("en-CA")
                            : item.created_at
                              ? new Date(item.created_at).toLocaleDateString("en-CA")
                              : "-"}
                        </td>
                        <td>{item.invoice_no || "-"}</td>
                        <td>{item.product_name || "-"}</td>
                        <td>{item.category_name || "-"}</td>
                        <td>{item.supplier_name || "-"}</td>
                        <td>{item.qty}</td>
                        <td>{item.unit || "-"}</td>
                        <td>{Number(item.price || 0).toFixed(2)}</td>
                        <td>
                          <span className={"badge " + qualityBadgeClass(item.quality_check)}>
                            {item.quality_check || "pending"}
                          </span>
                        </td>
                        <td>{item.remark || "-"}</td>
                        <td>
                          <Button
                            size="sm"
                            style={{ backgroundColor: "#c4302b", border: "none" }}
                            onClick={() => handleDelete(item)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <span className="me-2">Rows per page</span>
              <Form.Select
                value={showAll ? "all" : rowsPerPage}
                onChange={(e) => {
                  if (e.target.value === "all") {
                    setShowAll(true);
                    setRowsPerPage(filteredEntries.length || 5);
                  } else {
                    setShowAll(false);
                    setRowsPerPage(Number(e.target.value));
                  }
                  setCurrentPage(1);
                }}
                style={{ width: "90px", marginRight: "15px" }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value="all">All</option>
              </Form.Select>

              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Prev
              </Button>
              <span className="mx-3">
                {currentPage} / {totalPages || 1}
              </span>
              <Button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </Card>
        </>
      )}

      {showForm && (
        <Card body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Card.Title className="mb-0">Add to Stock</Card.Title>
            <Button
              style={{ backgroundColor: "#663399", border: "none" }}
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
            >
              Inward list
            </Button>
          </div>

          <Form>
            {/* GRN header - one per submission */}
            <Row className="mb-3">
              <Col md={3} className="mb-3">
                <Form.Label className="fw-semibold">GRN Chalan No</Form.Label>
                <Form.Control
                  value={grnNo || "..."}
                  readOnly
                  style={readOnlyStyle}
                />
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label className="fw-semibold">
                  Select Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={inwardDate}
                  onChange={(e) => setInwardDate(e.target.value)}
                />
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label className="fw-semibold">Enter Invoice Number</Form.Label>
                <Form.Control
                  placeholder="Enter Invoice Number"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                />
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label className="fw-semibold">
                  Select Supplier <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.supplier_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {/* Product picker - each pick appends a line to the table below */}
            <Row className="mb-3">
              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold">Select Category</Form.Label>
                <Form.Select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setProduct("");
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold">Select Product</Form.Label>
                <Form.Select
                  value={product}
                  onChange={(e) => handleProductChange(e.target.value)}
                >
                  <option value="">Select Product</option>
                  {productsForCategory.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.product_name}
                      {p.unit ? `_${p.unit}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <hr />

            <div style={{ overflowX: "auto" }}>
              <Table bordered className="align-middle">
                <thead style={{ backgroundColor: "#cfc7d9" }}>
                  <tr>
                    <th style={{ minWidth: "200px" }}>Product Name</th>
                    <th style={{ minWidth: "110px" }}>Qty</th>
                    <th style={{ minWidth: "90px" }}>Unit</th>
                    <th style={{ minWidth: "120px" }}>Price</th>
                    <th style={{ minWidth: "120px" }}>Amount</th>
                    <th style={{ minWidth: "180px" }}>Remark</th>
                    <th style={{ width: "60px" }} />
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        Select a category and product above to add rows.
                      </td>
                    </tr>
                  )}

                  {items.map((row, index) => (
                    <tr key={row.product_id}>
                      <td>
                        <Form.Control
                          value={row.product_name}
                          readOnly
                          style={readOnlyStyle}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          value={row.qty}
                          onChange={(e) =>
                            handleItemChange(index, "qty", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          value={row.unit}
                          readOnly
                          style={readOnlyStyle}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.unit_price}
                          onChange={(e) =>
                            handleItemChange(index, "unit_price", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          value={lineAmount(row).toFixed(2)}
                          readOnly
                          style={readOnlyStyle}
                        />
                      </td>
                      <td>
                        <Form.Control
                          value={row.remark}
                          onChange={(e) =>
                            handleItemChange(index, "remark", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          style={{ backgroundColor: "#c4302b", border: "none" }}
                          onClick={() => removeItem(index)}
                          title="Remove row"
                        >
                          <i className="i-Close" />
                        </Button>
                      </td>
                    </tr>
                  ))}

                  {items.length > 0 && (
                    <tr>
                      <td colSpan="4" className="text-end fw-semibold">
                        Total
                      </td>
                      <td className="fw-semibold">{grandTotal.toFixed(2)}</td>
                      <td colSpan="2" />
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
              >
                Back
              </Button>
              <Button
                style={{
                  backgroundColor: "#663399",
                  border: "none",
                  padding: "10px 40px",
                }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Submit"}
              </Button>
            </div>
          </Form>
        </Card>
      )}
    </section>
  );
}
